import { inject } from "fw";
import { handle } from "fw-state";
import { LogoutAction, StartAction } from "./actions";
import { once } from "helpers/once";
import { AppsyncResultsRepository } from "network/ats-appsync-repository";

import { AppSyncInfo, AGGREGATION_MAPPING, AppSyncFilter } from "models/monitor-appsync-results";
import { AtsOrganizationFilterAction, GridStore, SelectedFilterAction } from "./components/grid";

const AGGREGATIONS =
    "terms:(queuedCount @include:0) terms:(casMissingApps @include:0) terms:(atsMissingApps @include:0) sum:casApplicantCount sum:verifiedDocs sum:deliveredApps sum:deliveredDocs sum:queuedCount";

const FILTER_FIELDS_MAPPING: Map<string, string> = new Map()
    .set("cas", "casOrganizationId")
    .set("organization", "metaData.atsOrganizationId")
    .set("organizationCas", "organizationCas")
    .set("isLatest", "isLatest")
    .set("range", "syncStartedUtc")
    .set("status", "status")
    .set("casEnvironment", "casEnvironment");

const STATUS_MAPPING = {
    [AGGREGATION_MAPPING.totalInSync]: "queuedCount:0",
    [AGGREGATION_MAPPING.withAtsMissingApps]: "atsMissingApps:>0",
    [AGGREGATION_MAPPING.withCasMissingApps]: "casMissingApps:>0"
};
const CACHE_KEY = "ats-appsync-store";
const DEFAULT_FILTER = {
    isLatest: true
};
export class AppSyncCollapseItemAction {
    constructor(public id: string) {}
}

export class AppSyncNextPageAction {}
export class AppSyncPreviousPageAction {}
export class AppSyncEnsureAction {}
export class AppSyncRefreshAction {}
export class AppSyncResetAction {}

export class AppSyncFetchListAction<Filter> {
    constructor(
        public filter: Filter = null,
        public search: string = null,
        public sort: string = ""
    ) {}
}

@inject
export class AppSyncStore extends GridStore<AppSyncInfo, AppSyncFilter> {
    constructor(private network: AppsyncResultsRepository) {
        super(CACHE_KEY);
    }

    @handle(StartAction)
    private startAction(action: StartAction) {
        this.handleStartAction(action);
    }

    @handle(AppSyncCollapseItemAction)
    private collapseItemAction(action: AppSyncCollapseItemAction) {
        this.setState((state) => ({
            ...state,
            collapsedItem: action.id
        }));

        if (action.id) {
            this.loadResultById(action.id);
        }
    }

    @handle(SelectedFilterAction)
    private handleSelectedFilterAction(action: SelectedFilterAction) {
        if (!this.state.filter.isLatest) return;
        this.handleSelectedFilter(action);
    }

    @handle(LogoutAction)
    private logoutAction() {
        this.handleLogoutAction();
    }

    @handle(AppSyncEnsureAction)
    private async handleAtsOrganizationEnsureAction() {
        await once(CACHE_KEY, async () => {
            if (this.state.loaded && this.state.filter?.isLatest) return;
            this.restoreState();
            this.loadAppsyncResultsPageList();
        });
    }

    @handle(AppSyncRefreshAction)
    private refreshAction(action: AppSyncRefreshAction) {
        this.handleRefreshAction(action, () => {
            this.loadAppsyncResultsPageList();
        });
    }

    @handle(AppSyncResetAction)
    private resetAction(action: AppSyncResetAction) {
        this.handleResetAction(action, () => {
            this.loadAppsyncResultsPageList();
        });
    }

    @handle(AppSyncFetchListAction)
    private fetchListAction(action: AppSyncFetchListAction<AppSyncFilter>) {
        this.handleFetchList(action, () => {
            this.loadAppsyncResultsPageList();
        });
    }

    @handle(AtsOrganizationFilterAction)
    private handleAtsOrganizationFilterAction(action: AtsOrganizationFilterAction) {
        this.handleOrgFilterAction(action, () => {
            this.loadAppsyncResultsPageList();
        });
    }

    @handle(AppSyncNextPageAction)
    private nextPageAction(action: AppSyncNextPageAction) {
        this.handleNextPageAction(action, async (nextPageToken) => {
            await this.loadAppsyncResultsPageData(null, nextPageToken);
        });
    }

    @handle(AppSyncPreviousPageAction)
    private previousPageAction(action: AppSyncPreviousPageAction) {
        this.handlePreviousPage(action, async (previousPageToken) => {
            await this.loadAppsyncResultsPageData(previousPageToken, null);
        });
    }

    private async loadAppsyncResultsPageList() {
        const { currentPreviousPageToken, currentNextPageToken } = this.state;
        this.setState((state) => ({
            ...state,
            filter: {
                ...DEFAULT_FILTER,
                ...state.filter
            }
        }));
        await this.loadAppsyncResultsPageData(currentPreviousPageToken, currentNextPageToken);
    }

    private atsMissingApps(clientModelResponse): number {
        return clientModelResponse.Total - (clientModelResponse.Aggregations?.terms_atsMissingApps?.Items[0]?.Total ?? 0);
    }

    private casMissingApps(clientModelResponse): number {
        return clientModelResponse.Total - (clientModelResponse.Aggregations?.terms_casMissingApps?.Items[0]?.Total ?? 0);
    }

    private totalInSync(clientModelResponse): number {
        return clientModelResponse.Aggregations?.terms_queuedCount?.Items[0]?.Total ?? 0;
    }

    private allVerified(clientModelResponse): number {
        return (clientModelResponse.Aggregations?.sum_casApplicantCount?.Value ?? 0) - clientModelResponse.Aggregations?.sum_queuedCount?.Value ?? 0;
    }

    private async loadAppsyncResultsPageData(previousPageToken: string, nextPageToken: string) {
        const { pageSize, sort, search, filter, filterCounter, statusCounts } = this.state;
        const filterLength = Object.keys(filter).length;
        let withAtsMissingApps, withCasMissingApps, totalInSync, allVerified;
        let newStatusCounts = [];

        try {
            const clientModelResponse = await this.network.appSyncList(
                this.getWholeAppsyncFilter(),
                search,
                this.getOrgFilter(),
                sort,
                previousPageToken,
                nextPageToken,
                null,
                AGGREGATIONS,
                false,
                pageSize
            );
            newStatusCounts = clientModelResponse.Aggregations;

            withAtsMissingApps = this.atsMissingApps(clientModelResponse);
            withCasMissingApps = this.casMissingApps(clientModelResponse);
            totalInSync = this.totalInSync(clientModelResponse);
            allVerified = this.allVerified(clientModelResponse);

            if (typeof filter.status === "number" && filterLength === 2) {
                newStatusCounts = statusCounts;
                withAtsMissingApps = newStatusCounts["withAtsMissingApps"].Value;
                withCasMissingApps = newStatusCounts["withCasMissingApps"].Value;
                totalInSync = newStatusCounts["totalInSync"].Value;
                allVerified = newStatusCounts["allVerified"].Value;
            }

            if (typeof filter.status === "number" && (filterLength > 2 || filterLength < filterCounter)) {
                const clientModelResponseExceptStatus = await this.network.appSyncList(
                    this.getWholeAppsyncFilter(false),
                    search,
                    this.getOrgFilter(),
                    sort,
                    previousPageToken,
                    nextPageToken,
                    null,
                    AGGREGATIONS,
                    false,
                    pageSize
                );

                withAtsMissingApps = this.atsMissingApps(clientModelResponseExceptStatus);
                withCasMissingApps = this.casMissingApps(clientModelResponseExceptStatus);
                totalInSync = this.totalInSync(clientModelResponseExceptStatus);
                allVerified = this.allVerified(clientModelResponseExceptStatus);
            }

            newStatusCounts["withAtsMissingApps"] = {
                Value: withAtsMissingApps
            };
            newStatusCounts["withCasMissingApps"] = {
                Value: withCasMissingApps
            };
            newStatusCounts["totalInSync"] = {
                Value: totalInSync
            };
            newStatusCounts["allVerified"] = {
                Value: allVerified
            };

            this.setState((state) => ({
                ...state,
                loaded: true,
                updatedDate: new Date(),
                filterCounter: filterLength,
                statusCounts: newStatusCounts,
                previousPageToken: clientModelResponse.PreviousPageToken,
                nextPageToken: clientModelResponse.NextPageToken,
                currentPreviousPageToken: previousPageToken,
                currentNextPageToken: nextPageToken,
                list: clientModelResponse.Results,
                total: clientModelResponse.Total
            }));
        } catch (err) {
            this.setState((state) => ({ ...state, loaded: true, errorLoading: true }));
        }
    }

    private async loadResultById(id: string) {
        try {
            // We are replacing search query with an id string to get a single result
            const clientModelResponse = await this.network.appSyncResultById(id);
            if (clientModelResponse) {
                this.setState((state) => ({
                    ...state,
                    loaded: true,
                    list: this.state.list.map((task) => {
                        return task.Id === clientModelResponse.Id
                            ? {
                                  ...task,
                                  Result: clientModelResponse
                              }
                            : task;
                    })
                }));
            }
        } catch (err) {
            this.setState((state) => ({ ...state, loaded: true, errorLoading: true }));
        }
    }

    public getWholeAppsyncFilter(withStatus = true): string {
        const { filter } = this.state;

        const filterArgs = [];
        for (const [key, value] of Object.entries(filter)) {
            if (value === undefined || value === null) continue;

            if (!withStatus && key === "status") continue;

            if (FILTER_FIELDS_MAPPING.get(key) === undefined) continue;

            if (Array.isArray(value) && value.length) {
                (value as Array<string>).forEach((element) => {
                    filterArgs.push(`${FILTER_FIELDS_MAPPING.get(key)}:${element}`);
                });
            } else {
                switch (key) {
                    case "organizationCas":
                        filterArgs.push(value);
                        break;

                    case "status":
                        filterArgs.push(STATUS_MAPPING[value as number]);
                        break;

                    default:
                        filterArgs.push(`${FILTER_FIELDS_MAPPING.get(key)}:${value}`);
                }
            }
        }

        return filterArgs.join(" AND ");
    }

    private getOrgFilter() {
        const { tags, type } = this.state.orgFilter;

        if (type === null && !tags?.length) return null;

        const filterArgs = [];
        if (type !== null) filterArgs.push(`metaData.type:${type}`);
        if (tags?.length) filterArgs.push(...tags.map((tag) => `tags:("${tag}")`));

        return filterArgs.join(" AND ");
    }
}
